import React from 'react';
import PropTypes from 'prop-types';
import { formatNumber, getOrdinalSuffix, calculateCurrentPayouts } from '../utils/contestUtils';
import './EntrantPayoutSection.css';

/**
 * A reusable component to display contest payouts
 * @param {Object} props
 * @param {Array} props.payouts - The payout amounts if already provided
 * @param {Object} props.contest - The contest object (used to calculate payouts if not directly provided)
 */
const EntrantPayoutSection = ({ payouts: providedPayouts, contest }) => {
  // Get payouts - either from props, from the database, or calculate them dynamically
  const getPayouts = () => {
    // If payouts are provided directly as props, use them
    if (providedPayouts && providedPayouts.length > 0) {
      return providedPayouts;
    }
    
    // If contest has payouts defined, use those
    if (contest.entrantPayouts && contest.entrantPayouts.length > 0) {
      return contest.entrantPayouts;
    }
    
    // If we have entrants and an entry fee, calculate estimated payouts
    if (contest.entrants.length > 0 && contest.entryFee) {
      return calculateCurrentPayouts(contest.entryFee * contest.entrants.length, contest.entrants.length);
    }
    
    // If we can't calculate payouts, return empty array (will show TBD)
    return [];
  };

  const payouts = getPayouts();
  
  // Calculate total prize pool
  const totalPrizePool = payouts.reduce((sum, payout) => sum + payout, 0);
  
  // Determine if we should show the prize pool (only if there are payouts)
  const showPrizePool = payouts.length > 0;

  return (
    <div className="entrant-payouts-section">
      <p className="entrant-payouts-title">
        {/* Wrap the main title text in a span */}
        <span className="main-title-text">Payouts</span> 
        {showPrizePool && <span className="entrant-payouts-total-prize-pool"> (${formatNumber(totalPrizePool)})</span>}
      </p>
      {(payouts.length === 0) ? (
        <p className="payout">TBD</p>
      ) : (
        <ul>
          {payouts.map((payout, index) => (
            <li key={index}>
              <span className="place">{getOrdinalSuffix(index + 1)}</span>: 
              <span className="payout"> ${formatNumber(payout)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

EntrantPayoutSection.propTypes = {
  payouts: PropTypes.arrayOf(PropTypes.number),
  contest: PropTypes.shape({
    entrantPayouts: PropTypes.arrayOf(PropTypes.number),
    entrants: PropTypes.array.isRequired,
    entryFee: PropTypes.number.isRequired
  }).isRequired
};

EntrantPayoutSection.defaultProps = {
  payouts: []
};

export default EntrantPayoutSection;
